// Pure, client-safe display helpers for the profile starter pack. Deliberately
// separate from `StarterPackFlank.tsx` (which may only export components, per
// react-refresh) and from `profileStarterPack.ts` (which pulls in the model
// graph) — the only thing borrowed from the latter is an erased `import type`.
import type {
  StarterPackDisplayMode,
  StarterPackPayload,
} from "./profileStarterPack";
import type { GridRecommendation } from "../types";

export type StarterPackRec = StarterPackPayload["recommendations"][number];

/**
 * A pack rec is never members-only (the write path rejects the combo), so in
 * practice these always take the `public` branch — but `GridRecommendation` is
 * a union and the paywalled arm carries different field names, so read through
 * these rather than reaching for `.name` / `.image_url` directly.
 */
export function starterPackRecImageUrl(rec: GridRecommendation): string | null {
  return rec.cardType === "paywalled" ? rec.preview_image_url : rec.image_url;
}

export function starterPackRecTitle(rec: GridRecommendation): string {
  return rec.cardType === "paywalled" ? rec.public_title || "" : rec.name;
}

export function starterPackRecDescription(
  rec: GridRecommendation,
): string | null {
  return rec.cardType === "paywalled" ? null : rec.description;
}

/**
 * The slice of a rec the editors need. Both the pack payload and the picker's
 * search results are `GridRecommendation`s, whose two arms name their title
 * and image differently — normalize once on the way in.
 */
export type StarterPackSlotRec = {
  id: number;
  name: string;
  image_url: string | null;
};

export function starterPackSlotRec(rec: GridRecommendation): StarterPackSlotRec {
  return rec.cardType === "paywalled"
    ? {
        id: rec.id,
        name: rec.public_title || "Members-only rec",
        image_url: rec.preview_image_url,
      }
    : { id: rec.id, name: rec.name, image_url: rec.image_url };
}

/**
 * A rec can't join a pack if it's members-only or answers a specific request.
 * The server rejects both, but filtering client-side keeps pickers from
 * offering a choice that can only fail.
 */
export function isStarterPackEligible(rec: GridRecommendation): boolean {
  if (rec.cardType === "paywalled") return false;
  return !rec.members_only && !rec.limited_to_request && !rec.is_invite_rec;
}

/** Slot-indexed askew angles, so the four tiles never read as a rigid strip. */
export const STARTER_PACK_SLOT_ROTATIONS = [
  "-rotate-3",
  "rotate-2",
  "-rotate-2",
  "rotate-3",
] as const;

/** Degrees behind {@link STARTER_PACK_SLOT_ROTATIONS}; keep the two in step. */
export const STARTER_PACK_SLOT_ASKEW_DEG = [-3, 2, -2, 3] as const;

/**
 * Owner-facing names for the three display modes. A `Record` keyed by the union
 * on purpose: a new server-side mode fails the build here until it gets a
 * label, and {@link STARTER_PACK_DISPLAY_MODE_OPTIONS} is derived from it so
 * the dashboard picker can never fall out of sync with the union.
 */
export const STARTER_PACK_DISPLAY_MODE_LABELS: Record<
  StarterPackDisplayMode,
  string
> = {
  orbit: "Orbit",
  pinwheel: "Pinwheel",
  dvd: "DVD",
};

export const STARTER_PACK_DISPLAY_MODE_OPTIONS = Object.keys(
  STARTER_PACK_DISPLAY_MODE_LABELS,
) as StarterPackDisplayMode[];

/*
 * ---------------------------------------------------------------------------
 * Stage geometry
 *
 * The web starter-pack page paints the same square stage the mobile showcase
 * does (`mobile/components/starterPack/StarterPackShowcase.tsx`), which is the
 * reference implementation for `pinwheel` and `dvd` — the numbers below are
 * lifted from it so the two clients render the same arrangement. Everything is
 * a PERCENTAGE of the (square) stage, never a pixel, so the whole thing scales
 * with its container and never needs a resize listener.
 * ---------------------------------------------------------------------------
 */

/** Thumbnail edge length, % of stage, in the pinwheel and DVD modes. */
export const STARTER_PACK_THUMB_PERCENT = 18;

/**
 * Pinwheel: two concentric hairline rings, as diameters (% of stage). The
 * outer ring is inscribed in the stage's bordered square (full width), so the
 * crosshair reads as spokes rather than a floating crosshair.
 */
export const PINWHEEL_RING_DIAMETERS = [56, 100] as const;
/** Pinwheel: thumbnail center distance from the stage center (% of stage). */
export const PINWHEEL_THUMB_RADIUS_PERCENT = 36.5;
/** Slot-indexed diagonals, degrees clockwise from 12 o'clock. */
export const PINWHEEL_ANGLE_FOR_SLOT = [315, 225, 45, 135] as const;
/**
 * Slow in-place spin. Non-harmonic periods and alternating directions, so the
 * four tiles never read as one synchronized mechanism.
 */
export const PINWHEEL_SPIN_PERIODS_S = [26, 34, 30, 38] as const;
export const PINWHEEL_SPIN_DIRECTIONS = [1, -1, 1, -1] as const;

/**
 * DVD: the collage "bounce" effect's math (mobile `collageAnimationMath.ts`
 * `computeBouncePosition`) — a seeded launch angle and speed per slot, constant
 * velocity, elastic reflection off the stage walls. Speeds are authored against
 * a 1080-unit reference canvas and scale to the stage exactly as the collage
 * scales to its container.
 */
const DVD_REFERENCE_CANVAS = 1080;
/**
 * Where each thumb sits at t=0: the same diagonals as the pinwheel (a little
 * tighter), so a fresh load — and a reduced-motion viewer, who never leaves
 * t=0 — reads as a composed arrangement rather than four random specks.
 */
export const DVD_HOME_RADIUS_PERCENT = 34;

/** Half the wall-to-wall travel available to a thumb's CENTER, % of stage. */
export const STARTER_PACK_BOUNCE_HALF_SPAN_PERCENT =
  (100 - STARTER_PACK_THUMB_PERCENT) / 2;

/**
 * Orbit mode: one ring per slot, as diameters (% of stage) with per-slot
 * periods and start angles (degrees clockwise from 12 o'clock). 265°/275° put
 * the first two tiles just left of the avatar and 85°/95° just right of it,
 * so t=0 reads as the profile flank's arrangement. Shared by the animated
 * detail stage and the frozen share asset.
 */
export const STARTER_PACK_ORBITS = [
  { diameter: 54, period: 46, startAngle: 265 },
  { diameter: 68, period: 61, startAngle: 275 },
  { diameter: 82, period: 79, startAngle: 95 },
  { diameter: 96, period: 97, startAngle: 85 },
] as const;

/** Diameter (% of stage) of the ring the curved text is set on. */
export const STARTER_PACK_TEXT_RING_DIAMETER = 40;

/**
 * Orbit mode's decorative dots, a small handful per ring — lifted from the
 * mobile showcase (`showcaseGeometry.ts` ORBIT_DOTS, fractions × 100). Each
 * rides its ring at the ring's own period, so its angular offset from that
 * ring's thumbnail is constant and they can never collide. The dot tint is
 * translucent, so it renders over a solid disc of the page background that
 * blots out the rail line beneath it.
 */
export const STARTER_PACK_ORBIT_DOTS: {
  ring: number;
  /** Dot diameter, % of stage. */
  sizePercent: number;
  /** Degrees clockwise from 12 o'clock at t=0. */
  startAngle: number;
}[] = [
  { ring: 0, sizePercent: 3, startAngle: 60 },
  { ring: 0, sizePercent: 1.4, startAngle: 170 },
  { ring: 1, sizePercent: 1.2, startAngle: 200 },
  { ring: 1, sizePercent: 2.8, startAngle: 330 },
  { ring: 1, sizePercent: 2.2, startAngle: 285 },
  { ring: 2, sizePercent: 1.6, startAngle: 130 },
  { ring: 2, sizePercent: 2.6, startAngle: 15 },
  { ring: 3, sizePercent: 3.2, startAngle: 20 },
  { ring: 3, sizePercent: 2, startAngle: 160 },
  { ring: 3, sizePercent: 1.5, startAngle: 250 },
];

/**
 * A slot's frozen (t=0) center offset from the stage center, % of stage —
 * what the share asset renders and what a reduced-motion DVD viewer sees.
 */
export function starterPackFrozenPosition(
  mode: StarterPackDisplayMode,
  slotIndex: number,
): { xPercent: number; yPercent: number } {
  if (mode === "pinwheel" || mode === "dvd") {
    const radius =
      mode === "pinwheel"
        ? PINWHEEL_THUMB_RADIUS_PERCENT
        : DVD_HOME_RADIUS_PERCENT;
    const angle =
      (PINWHEEL_ANGLE_FOR_SLOT[slotIndex % PINWHEEL_ANGLE_FOR_SLOT.length] *
        Math.PI) /
      180;
    return {
      xPercent: radius * Math.sin(angle),
      yPercent: -radius * Math.cos(angle),
    };
  }
  const orbit = STARTER_PACK_ORBITS[slotIndex % STARTER_PACK_ORBITS.length];
  const angle = (orbit.startAngle * Math.PI) / 180;
  return {
    xPercent: (orbit.diameter / 2) * Math.sin(angle),
    yPercent: -(orbit.diameter / 2) * Math.cos(angle),
  };
}

export type StarterPackBounceAxisTiming = {
  /** One wall-to-wall traversal, seconds. CSS `alternate` handles the return. */
  periodSeconds: number;
  /** Seconds to NEGATE into `animation-delay` to land t=0 on the home spot. */
  delaySeconds: number;
};

export type StarterPackBounceTiming = {
  x: StarterPackBounceAxisTiming;
  y: StarterPackBounceAxisTiming;
};

/**
 * Constant velocity plus elastic walls is a triangle wave per axis, which is
 * exactly what a `linear` + `alternate` CSS animation between the two extremes
 * traces out — so the DVD bounce needs no JS clock and no per-frame work. All
 * that's left is to solve, per axis, for the period (one traversal) and for the
 * phase offset that puts t=0 on the home spot moving in the seeded direction.
 *
 * The period is stage-size independent: span and speed both scale linearly with
 * the stage, so the ratio cancels and one set of durations is correct at every
 * breakpoint.
 */
export function starterPackBounceTiming(
  slotIndex: number,
): StarterPackBounceTiming {
  const halfSpan = STARTER_PACK_BOUNCE_HALF_SPAN_PERCENT;
  const span = halfSpan * 2;

  const seed = slotIndex * 137.5 + 0.5;
  const launchAngle = seed % (2 * Math.PI);
  // % of stage per second (the collage's px/s, restated in stage percentages).
  const speed = ((90 + ((seed * 7) % 90)) * 100) / DVD_REFERENCE_CANVAS;

  const pinAngle =
    (PINWHEEL_ANGLE_FOR_SLOT[slotIndex % PINWHEEL_ANGLE_FOR_SLOT.length] *
      Math.PI) /
    180;

  const axis = (
    home: number,
    velocity: number,
  ): StarterPackBounceAxisTiming => {
    // A near-zero component would divide out to an infinite period; clamp it to
    // "so slow it may as well be still" rather than emit an invalid duration.
    const speedOnAxis = Math.max(Math.abs(velocity), 1e-3);
    const periodSeconds = span / speedOnAxis;
    // Fraction of the forward (-halfSpan → +halfSpan) leg the home spot sits at.
    const fraction = (home + halfSpan) / span;
    // Moving in +: home is on the forward leg. Moving in -: it's on the return
    // leg, which `alternate` places in the animation's second iteration.
    const delaySeconds =
      velocity >= 0
        ? fraction * periodSeconds
        : periodSeconds + (1 - fraction) * periodSeconds;
    // Rounded to a millisecond: full float precision differs between the
    // server's and the browser's math in the last bits, which React reports
    // as a hydration style mismatch on every DVD tile.
    return {
      periodSeconds: Math.round(periodSeconds * 1000) / 1000,
      delaySeconds: Math.round(delaySeconds * 1000) / 1000,
    };
  };

  return {
    x: axis(
      DVD_HOME_RADIUS_PERCENT * Math.sin(pinAngle),
      speed * Math.cos(launchAngle),
    ),
    y: axis(
      -DVD_HOME_RADIUS_PERCENT * Math.cos(pinAngle),
      speed * Math.sin(launchAngle),
    ),
  };
}

/**
 * Whether a payload should paint tiles around the avatar at all. Hidden packs
 * are invisible even to their owner (the owner un-hides from the dashboard),
 * and a non-owner never receives an incomplete payload in the first place.
 */
export function hasVisibleStarterPack(
  starterPack: StarterPackPayload | null | undefined,
): boolean {
  if (!starterPack || starterPack.is_hidden) return false;
  if (starterPack.is_complete) return true;
  // Pending: the owner's own in-progress pack, with "+" slots.
  return starterPack.is_owner && starterPack.recommendations.length > 0;
}
